import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios for token-based auth and cookies
axios.interceptors.request.use((config) => {
  // Priority 1: Session token (for OAuth login) - always send if available
  const sessionToken = localStorage.getItem('session_token');
  if (sessionToken && config.url?.includes('/api/')) {
    config.headers['X-Session-Token'] = sessionToken;
    // Debug log (remove in production)
    if (process.env.NODE_ENV === 'development') {
      console.log('Sending session token in header:', sessionToken.substring(0, 20) + '...');
    }
  } else if (config.url?.includes('/api/') && !config.url?.includes('/auth/')) {
    // Only warn for non-auth endpoints
    console.warn('No session token found in localStorage for API call:', config.url);
  }
  
  // Priority 2: JWT token (for email/password login) - only if no session token
  if (!sessionToken) {
    const token = localStorage.getItem('access_token');
    if (token && config.url?.includes('/api/') && !config.headers['X-Session-ID']) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  
  // Always include credentials for cross-domain cookie support
  config.withCredentials = true;
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const refreshToken = localStorage.getItem('refresh_token');
      // Don't try to refresh mock tokens or if we're already on the refresh endpoint
      if (refreshToken && !refreshToken.includes('mock') && !originalRequest.url?.includes('/auth/refresh')) {
        try {
          const response = await axios.post(`${API}/auth/refresh`, {
            refresh_token: refreshToken
          });
          
          const { access_token, refresh_token: newRefreshToken } = response.data;
          localStorage.setItem('access_token', access_token);
          localStorage.setItem('refresh_token', newRefreshToken);
          
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return axios(originalRequest);
        } catch (refreshError) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          // Don't redirect if we're already on the landing page
          if (window.location.pathname !== '/') {
            window.location.href = '/';
          }
        }
      } else if (refreshToken && refreshToken.includes('mock')) {
        // Clear mock tokens silently
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
      }
    }
    
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isProcessingSession, setIsProcessingSession] = useState(false);
  const [error, setError] = useState(null);

  // Separate useEffect for OAuth session processing to avoid race conditions
  useEffect(() => {
    const processOAuthSession = async () => {
      const hash = window.location.hash;
      const query = window.location.search;
      const hasHashSession = hash && hash.includes('session_id=');
      const hasQuerySession = query && query.includes('session_id=');
      
      if (hasHashSession || hasQuerySession) {
        setIsProcessingSession(true);
        setLoading(true);
        
        const raw = hasHashSession ? hash : query;
        const sessionId = raw.split('session_id=')[1].split(/[&#?]/)[0];
        
        try {
          // Add a small delay to ensure backend is ready
          await new Promise(resolve => setTimeout(resolve, 500));
          
          let response;
          try {
            response = await axios.post(
            `${API}/auth/session`,
            {},
            {
              headers: { 'X-Session-ID': sessionId },
              withCredentials: true,
                timeout: 10000
              }
            );
          } catch (e) {
            // Fallback: GET with query param (some proxies strip custom headers)
            response = await axios.get(`${API}/auth/session`, {
              params: { session_id: sessionId },
              withCredentials: true,
              timeout: 10000
            });
          }
          
          setUser(response.data.user);
          // Store session token in localStorage as fallback for cross-domain cookie issues
          if (response.data.session_token) {
            localStorage.setItem('session_token', response.data.session_token);
            console.log('Session token stored in localStorage');
          }
          // Clean up URL and ensure we land on dashboard
          window.history.replaceState(null, '', '/dashboard');
          setError(null);
          
          // Let Landing page handle redirect for authenticated users (removes race condition)
        } catch (error) {
          console.error('OAuth session creation failed:', error);
          setError('Authentication failed. Please try signing in again.');
          // Clean up URL even on error
          window.history.replaceState(null, '', window.location.pathname);
        } finally {
          setIsProcessingSession(false);
          setLoading(false);
        }
      }
    };

    processOAuthSession();
  }, []);

  // Separate useEffect for initial auth check (only if not processing OAuth)
  useEffect(() => {
    const initAuth = async () => {
      // Skip if already processing OAuth session
      const hash = window.location.hash;
      const query = window.location.search;
      if ((hash && hash.includes('session_id=')) || (query && query.includes('session_id='))) {
        return; // Let processOAuthSession handle it
      }
      
      setLoading(true);
      setError(null);
      
      // Check for JWT tokens first
      const accessToken = localStorage.getItem('access_token');
      if (accessToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            withCredentials: true
          });
          setUser(response.data);
          // Store session token if provided
          if (response.data && response.data.session_token) {
            localStorage.setItem('session_token', response.data.session_token);
          }
          setLoading(false);
          return;
        } catch (error) {
          // Token might be expired, clear storage
          console.log('Access token expired, clearing storage...');
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
        }
      }

      // Check existing session (OAuth) - this should not show errors for new users
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true
        });
        setUser(response.data);
        // Store session token in localStorage if provided (for cross-domain cookie fallback)
        if (response.data && response.data.session_token) {
          localStorage.setItem('session_token', response.data.session_token);
          console.log('Session token stored in localStorage from /auth/me');
        }
      } catch (error) {
        // This is normal for new users - no error needed
        console.log('No existing session found');
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // Google OAuth via Emergent (re-enabled)
  const login = () => {
    const redirectUrl = `${window.location.origin}/dashboard`;
    const loginUrl = `${BACKEND_URL}/api/auth/google/login?redirect=${encodeURIComponent(redirectUrl)}`;
    window.location.href = loginUrl;
  };

  // Microsoft OAuth removed - Google OAuth only

  // Email/password registration
  const register = async (userData) => {
    setError(null);
    try {
      const response = await axios.post(`${API}/auth/register`, userData);
      const { access_token, refresh_token } = response.data;
      
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      
      // Get user data
      const userResponse = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(userResponse.data);
      
      return userResponse.data;
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      setError(message);
      throw new Error(message);
    }
  };

  // Email/password login
  const loginWithEmail = async (email, password) => {
    setError(null);
    try {
      const formData = new FormData();
      formData.append('username', email);
      formData.append('password', password);
      
      const response = await axios.post(`${API}/auth/login`, formData);
      const { access_token, refresh_token } = response.data;
      
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      
      // Get user data
      const userResponse = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(userResponse.data);
      
      return userResponse.data;
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      setError(message);
      throw new Error(message);
    }
  };

  const logout = async () => {
    try {
      // Logout from OAuth session if exists
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout API call failed:', error);
      // Continue with local cleanup even if API call fails
    } finally {
      // Clear all authentication data
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('session_token');
      
      // Clear any other Bissa-related data
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('bissa_') || key.startsWith('Bissa'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      // Clear session storage
      sessionStorage.clear();
      
      // Clear user state
      setUser(null);
      
      // Force redirect to homepage (use replace to prevent back button issues)
      window.location.replace('/');
    }
  };

  const clearError = () => {
    setError(null);
  };

  // Authenticated fetch function for API calls
  const authFetch = async (url, options = {}) => {
    // Priority 1: Session token (for OAuth login) - always send if available
    const sessionToken = localStorage.getItem('session_token');
    
    // Priority 2: JWT token (for email/password login) - only if no session token
    const token = !sessionToken ? localStorage.getItem('access_token') : null;
    
    const config = {
      ...options,
      credentials: 'include', // Always include credentials for OAuth sessions
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    };

    // Add session token header if available
    if (sessionToken && url.includes('/api/')) {
      config.headers['X-Session-Token'] = sessionToken;
    }
    
    // Add Authorization header if we have a valid JWT token (not mock) and no session token
    if (token && !token.includes('mock') && !sessionToken && !config.headers['X-Session-ID']) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, config);
      
      // If we get 401 and have a token, the token might be expired
      if (response.status === 401 && token) {
        console.log('Token expired, clearing localStorage');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
      }
      
      return response;
    } catch (error) {
      console.error('AuthFetch error:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      setUser,
      loading, 
      error,
      login, 
      loginWithEmail,
      register,
      logout, 
      clearError,
      isProcessingSession,
      authFetch
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};